import {
  Container,
  Box,
  Header,
  Button,
  Grid,
  SpaceBetween,
  Link,
  SegmentedControl,
  StatusIndicator,
  Icon,
  Input,
  Alert,
  Table,
  Pagination,
  ExpandableSection,
  FormField,
  Autosuggest,
  Select,
  SelectProps,
} from '@cloudscape-design/components';

import { ViewLayout } from '../../components/ViewLayout';
import { useEffect, useMemo, useState } from 'react';
import { StatisticBox } from '../../components/StatisticBox';
import ReportCardHeader from '../../components/ReportCardHeader';
import { useMutation } from '@tanstack/react-query';
import { createReport, createReportPublic } from '../../common/api/reports';
import { LoadingOrError } from '../../components/Loading';
import { useNavigate } from 'react-router-dom';
import { DropdownStatusProps } from '@cloudscape-design/components/internal/components/dropdown-status';
import AutoCompleteSearch, { Suggestion } from '../../components/AutoCompleteSearch';
import { BUSINESS_TYPES } from '../../common/types';
import { getBusinessType } from '../../common/businessTypes';

const STORAGE_KEY = 'contact_form_data';

type ContactFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  title: string;
  referenceName: string;
}

export default function View() {
  const navigate = useNavigate();

  const [selectedItems, setSelectedItems] = useState<Suggestion[]>([]);

  const selectedSuggestion = selectedItems.length > 0 ? selectedItems[0] : undefined;

  const createMutation = useMutation({
    mutationFn: createReportPublic,
    onSuccess: (data) => {
      navigate(`/create/${data.public_code}`);
    },
  });

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [referenceName, setReferenceName] = useState<string>('');
  const [businessType, setBusinessType] = useState<SelectProps.Option | null>(null);

  const [showErrors, setShowErrors] = useState<boolean>(false);

  // Load saved form data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData: ContactFormData = JSON.parse(savedData);
        setFirstName(parsedData.firstName || '');
        setLastName(parsedData.lastName || '');
        setEmail(parsedData.email || '');
        setPhoneNumber(parsedData.phoneNumber || '');
        setTitle(parsedData.title || '');
        setReferenceName(parsedData.referenceName || '');
      } catch (error) {
        console.error('Error parsing saved form data:', error);
      }
    }
  }, []);

  const validPhoneNumber = useMemo(() => {
    if (!phoneNumber.trim()) {
      return false;
    }
    return phoneNumber.trim().length >= 10 && phoneNumber.trim().length <= 15;
  }, [phoneNumber]);

  const validEmail = useMemo(() => {
    if (!email.trim()) {
      return false;
    }
    return email.trim().includes('@');
  }, [email]);

  const invalidContact = useMemo(() => {
    if (!firstName.trim() || !lastName.trim() || !validEmail || !validPhoneNumber) {
      return true;
    }
    return false;
  }, [firstName, lastName, email, phoneNumber]);

  const anyContact = useMemo(() => {
    return !!(
      firstName.trim() ||
      lastName.trim() ||
      email.trim() ||
      phoneNumber.trim() ||
      title.trim()
    );
  }, [firstName, lastName, email, phoneNumber, title]);

  const handleCreate = () => {
    if (createMutation.isPending) {
      return;
    }
    if (!selectedSuggestion) {
      return;
    }
    if (!selectedSuggestion.placeId) {
      return;
    }

    if (!anyContact) {
      setShowErrors(true);
      return;
    }

    if (invalidContact) {
      setShowErrors(true);
      return;
    }

    // Save form data to local storage
    const formData: ContactFormData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phoneNumber: phoneNumber.trim(),
      title: title.trim(),
      referenceName: referenceName.trim(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));

    const contact = {
      first_name: firstName.trim(),
      last_name: lastName.trim() || undefined,
      email: email.trim() || undefined,
      phone_number: phoneNumber.trim() || undefined,
      title: title.trim() || undefined,
      reference_name: referenceName.trim() || undefined,
    };

    createMutation.mutate({
      place_id: selectedSuggestion.placeId,
      contact,
      business_type:
        businessType?.value === 'Other' ? undefined : (businessType?.value ?? undefined),
    });
  };

  useEffect(() => {
    if (selectedItems.length > 0) {
      // try to find the best business type
      const firstItem = selectedItems[0];
      const googleTags = firstItem.tags;
      const businessType = getBusinessType(googleTags);
      if (businessType) {
        setBusinessType({ label: businessType, value: businessType });
      } else {
        setBusinessType({ label: 'Other', value: 'Other' });
      }
    }
  }, [selectedItems]);

  return (
    <ViewLayout
      header={<ReportCardHeader />}
      contentType="dashboard"
      navigationHide
      headerVariant="high-contrast"
    >
      <Grid
        gridDefinition={[
          {
            colspan: {
              default: 12,
            },
          },
        ]}
      >
        <Container
          header={
            <Header
              variant="h1"
              description="Enter a business name to create a Reputation Scorecard from Google Reviews"
            >
              Create a Reputation Scorecard
            </Header>
          }
        >
          <SpaceBetween direction="vertical" size="s">
            <SpaceBetween direction="horizontal" size="xxs">
              <Box variant="p" fontSize="heading-s" color="text-status-error">
                *
              </Box>
              <Box variant="p" fontWeight="light" color="text-status-error">
                <i>Required</i>
              </Box>
            </SpaceBetween>
            <Grid
              gridDefinition={[
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
              ]}
            >
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xxs">
                    <Box variant="p" fontWeight="bold">
                      First Name
                    </Box>
                    <Box variant="p" fontSize="heading-s" color="text-status-error">
                      *
                    </Box>
                  </SpaceBetween>
                }
                errorText={
                  showErrors && anyContact && !firstName.trim()
                    ? 'First name is required'
                    : undefined
                }
              >
                <Input
                  value={firstName}
                  onChange={({ detail }) => setFirstName(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xxs">
                    <Box variant="p" fontWeight="bold">
                      Last Name
                    </Box>
                    <Box variant="p" fontSize="heading-s" color="text-status-error">
                      *
                    </Box>
                  </SpaceBetween>
                }
                errorText={
                  showErrors && anyContact && !lastName.trim() ? 'Last name is required' : undefined
                }
              >
                <Input
                  value={lastName}
                  onChange={({ detail }) => setLastName(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xxs">
                    <Box variant="p" fontWeight="bold">
                      Email
                    </Box>
                    <Box variant="p" fontSize="heading-s" color="text-status-error">
                      *
                    </Box>
                  </SpaceBetween>
                }
                errorText={
                  showErrors && anyContact && !validEmail ? 'Email is required' : undefined
                }
              >
                <Input
                  value={email}
                  onChange={({ detail }) => setEmail(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xxs">
                    <Box variant="p" fontWeight="bold">
                      Cell Phone Number
                    </Box>
                    <Box variant="p" fontSize="heading-s" color="text-status-error">
                      *
                    </Box>
                  </SpaceBetween>
                }
                errorText={
                  showErrors && anyContact && !validPhoneNumber
                    ? 'Phone number is required'
                    : undefined
                }
              >
                <Input
                  value={phoneNumber}
                  onChange={({ detail }) => setPhoneNumber(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField label="Title">
                <Input
                  value={title}
                  onChange={({ detail }) => setTitle(detail.value)}
                  placeholder="(optional)"
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField label="If You Have Already Spoken to a Feedback Representative Enter Their Name:">
                <Input
                  value={referenceName}
                  onChange={({ detail }) => setReferenceName(detail.value)}
                  placeholder="(optional)"
                  disabled={createMutation.isPending}
                />
              </FormField>
            </Grid>
            <FormField
              label={
                <SpaceBetween direction="horizontal" size="xxs">
                  <Box variant="p" fontWeight="bold">
                    Business Location
                  </Box>
                  <Box variant="p" fontSize="heading-s" color="text-status-error">
                    *
                  </Box>
                </SpaceBetween>
              }
              description="Start typing the business name or location and select from the dropdown"
            >
              <AutoCompleteSearch
                selectedItems={selectedItems}
                setSelectedItems={(items: Suggestion[]) =>
                  setSelectedItems(items.length > 0 ? [items[0]] : [])
                }
                loading={createMutation.isPending}
                searchDisabled={selectedItems.length > 0}
                hideSearch={selectedItems.length > 0}
                placeholder="Enter your business name..."
              />
            </FormField>
            <Grid gridDefinition={[{ colspan: { default: 12, xs: 6 } }]}>
              <FormField label="Business Type">
                <Select
                  selectedOption={businessType}
                  options={BUSINESS_TYPES}
                  onChange={({ detail }) => setBusinessType(detail.selectedOption)}
                  disabled={createMutation.isPending}
                  placeholder="Select a business type"
                />
              </FormField>
            </Grid>
            <Box float="right">
              <Button
                variant="primary"
                iconName="add-plus"
                onClick={handleCreate}
                loading={createMutation.isPending}
                disabled={!selectedSuggestion || !anyContact}
              >
                Create Scorecard
              </Button>
            </Box>
            {createMutation.isError && (
              <LoadingOrError loading={createMutation.isPending} error={createMutation.error} />
            )}
          </SpaceBetween>
        </Container>
      </Grid>
    </ViewLayout>
  );
}
